import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** Offizielle Event-Seiten der S-Arena (Ticketvorverkauf & Infos) */
const TICKET_DAYS = [
  {
    index: 1,
    href: "https://arena-bernau.de/de/event/18-chin-woo-kung-fu-weltmeisterschaft-tag-1-eroeffnungszeremonie",
  },
  {
    index: 2,
    href: "https://arena-bernau.de/de/event/18-chin-woo-kung-fu-weltmeisterschaft-tag-2-taolu-wettkaempfe",
  },
  {
    index: 3,
    href: "https://arena-bernau.de/de/event/18-chin-woo-kung-fu-weltmeisterschaft-tag-3-taolu-wettkaempfe",
  },
];

function TicketSaleCTA() {
  const { t } = useTranslation();

  const days = useMemo(
    () =>
      TICKET_DAYS.map((d) => {
        const p = `day${d.index}`;
        return {
          href: d.href,
          index: d.index,
          num: t(`ticketSale.${p}_num`),
          tag: t(`ticketSale.${p}_tag`),
          weekday: t(`ticketSale.${p}_weekday`),
          date: t(`ticketSale.${p}_date`),
          focus: t(`ticketSale.${p}_focus`),
          ariaLabel: t("ticketSale.link_aria", {
            weekday: t(`ticketSale.${p}_weekday`),
            date: t(`ticketSale.${p}_date`),
          }),
        };
      }),
    [t]
  );

  return (
    <section
      className="relative z-10 overflow-hidden bg-gradient-to-br from-primary via-primary to-primary-dark pt-14 pb-14 transition-colors dark:from-primary-light dark:via-primary dark:to-slate-900 md:pt-16 md:pb-16"
      id="ticket-sale"
      aria-labelledby="ticket-sale-heading"
    >
      <div
        className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-white/15 blur-3xl dark:bg-white/10"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-black/20 blur-3xl dark:bg-slate-950/40"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.08),transparent_50%)]"
        aria-hidden
      />

      <div className="relative z-[1] mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-2xl border border-white/25 bg-white/10 p-8 shadow-xl shadow-primary-dark/20 backdrop-blur-md dark:border-white/15 dark:bg-slate-950/35 dark:shadow-black/30 md:p-10 lg:p-12">
          <header className="mb-8 text-center md:mb-10">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-white/85 dark:text-white/90">
              {t("ticketSale.badge")}
            </p>
            <h2
              id="ticket-sale-heading"
              className="relative mb-5 inline-block text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-5xl"
            >
              {t("ticketSale.title")}
              <span
                className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-white/95"
                aria-hidden
              />
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/90 sm:text-lg md:text-xl">
              {t("ticketSale.description")}
            </p>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-white/80 sm:text-base">
              {t("ticketSale.days_intro")}
            </p>
          </header>

          <nav aria-label={t("ticketSale.title")}>
            <ol className="m-0 grid list-none grid-cols-1 gap-5 p-0 md:grid-cols-3 md:gap-4 lg:gap-5">
              {days.map((day) => (
                <li key={day.index} className="m-0 p-0">
                  <a
                    href={day.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={day.ariaLabel}
                    title={t("ticketSale.opens_external")}
                    className="group relative flex min-h-[240px] flex-col overflow-hidden rounded-2xl border border-white/25 bg-white/10 p-6 text-left text-white shadow-md transition-all duration-300 hover:border-white/45 hover:bg-white/[0.18] hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary motion-safe:hover:-translate-y-1 dark:focus-visible:ring-offset-slate-900 md:min-h-[260px] md:p-7"
                  >
                    <span
                      className="pointer-events-none absolute -right-1 top-0 select-none text-7xl font-black tabular-nums leading-none text-white/[0.11] transition-colors duration-300 group-hover:text-white/[0.2] md:text-8xl"
                      aria-hidden
                    >
                      {day.num}
                    </span>

                    <span className="relative mb-3 inline-flex w-fit rounded-full border border-white/35 bg-white/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white/95">
                      {day.tag}
                    </span>
                    <span className="relative text-xl font-semibold tracking-tight sm:text-2xl">
                      {day.weekday}
                    </span>
                    <span className="relative mt-1 text-sm text-white/85">
                      {day.date}
                    </span>
                    <span className="relative mt-3 border-l-2 border-white/35 pl-3 text-sm leading-snug text-white/80">
                      {day.focus}
                    </span>

                    <span className="relative mt-auto flex items-center gap-2.5 border-t border-white/20 pt-5">
                      <span className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-primary transition-colors group-hover:bg-white dark:text-primary sm:text-base">
                        {t("ticketSale.day_cta")}
                        <i
                          className="fas fa-external-link-alt text-xs opacity-75"
                          aria-hidden
                        />
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <p className="mt-10 text-center text-sm text-white/75 md:mt-12">
            {t("ticketSale.venue_line")}
          </p>
        </div>
      </div>
    </section>
  );
}

export default TicketSaleCTA;
